import React, { useState, useEffect, useRef } from 'react';

const OrganicParticles = ({ mousePosition, scrollY, isReducedMotion }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (isReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    // Test canvas rendering
    ctx.fillStyle = 'rgba(246, 65, 143, 0.3)';
    ctx.fillRect(50, 50, 100, 100);
    console.log('Canvas test square drawn');

    // Initialize particles with faster, independent movement
    const particleCount = Math.min(60, Math.floor(dimensions.width / 25));
    console.log('Initializing particles:', particleCount, 'dimensions:', dimensions);
    particlesRef.current = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * dimensions.width,
      y: Math.random() * dimensions.height,
      vx: (Math.random() - 0.5) * 1.5, // Increased base velocity
      vy: (Math.random() - 0.5) * 1.5, // Increased base velocity
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      hue: Math.random() > 0.5 ? 330 : 200, // Pink or blue hues
    }));

    const animate = () => {
      if (!ctx || !canvas) return;
      
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      
      if (particlesRef.current.length === 0) {
        console.log('No particles to animate');
        return;
      }

      particlesRef.current.forEach((particle, index) => {
        // Add subtle directional drift for more organic movement
        const time = Date.now() * 0.001;
        const driftX = Math.sin(time * 0.5 + index * 0.1) * 0.3;
        const driftY = Math.cos(time * 0.3 + index * 0.15) * 0.2;

        // Independent movement - no scroll influence
        particle.x += particle.vx + driftX;
        particle.y += particle.vy + driftY;

        // Maintain velocity with slight damping for organic feel
        particle.vx *= 0.995;
        particle.vy *= 0.995;

        // Add random velocity bursts to keep movement interesting
        if (Math.random() < 0.01) {
          particle.vx += (Math.random() - 0.5) * 0.5;
          particle.vy += (Math.random() - 0.5) * 0.5;
        }

        // Boundary wrapping
        if (particle.x < -10) particle.x = dimensions.width + 10;
        if (particle.x > dimensions.width + 10) particle.x = -10;
        if (particle.y < -10) particle.y = dimensions.height + 10;
        if (particle.y > dimensions.height + 10) particle.y = -10;

        // Breathing opacity effect
        const breathe = Math.sin(Date.now() * 0.002 + index * 0.5) * 0.15; // Faster breathing
        const currentOpacity = Math.max(0, Math.min(1, particle.opacity + breathe));

        // Draw particle with glow effect
        try {
          const gradient = ctx.createRadialGradient(
            particle.x, particle.y, 0,
            particle.x, particle.y, particle.size * 4
          );
          
          gradient.addColorStop(0, `hsla(${particle.hue}, 70%, 60%, ${currentOpacity})`);
          gradient.addColorStop(0.4, `hsla(${particle.hue}, 70%, 50%, ${currentOpacity * 0.4})`);
          gradient.addColorStop(1, `hsla(${particle.hue}, 70%, 40%, 0)`);

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size * 4, 0, Math.PI * 2);
          ctx.fill();

          // Core particle
          ctx.fillStyle = `hsla(${particle.hue}, 80%, 70%, ${currentOpacity * 0.8})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
        } catch (error) {
          console.warn('Error drawing particle:', error);
        }
      });

      // Draw subtle connections between nearby particles
      particlesRef.current.forEach((particle, i) => {
        particlesRef.current.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const opacity = (1 - distance / 150) * 0.15;
            ctx.strokeStyle = `rgba(246, 65, 143, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, isReducedMotion]);

  if (isReducedMotion) {
    return (
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'linear-gradient(135deg, rgba(246, 65, 143, 0.05) 0%, transparent 50%, rgba(97, 180, 217, 0.05) 100%)'
        }}
      />
    );
  }

  return (
    <div className="absolute inset-0" style={{ zIndex: 1 }}>
      {/* Debug element - remove this later */}
      <div 
        className="absolute top-4 left-4 text-green-400 text-sm z-50"
        style={{ zIndex: 1000 }}
      >
        {/*OrganicParticles Loaded: {dimensions.width}x{dimensions.height}*/}
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ 
          width: '100%', 
          height: '100%',
          zIndex: 1
        }}
      />
    </div>
  );
};

export default OrganicParticles; 